import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash, LinkSimple, ShieldWarning, Globe, CaretDown, Copy,
  MagnifyingGlass, Lightning, Star, Check, Spinner, Image, ArrowRight, PencilSimple, ShoppingCart, ArrowsClockwise
} from '@phosphor-icons/react';

const REGIONS = ['US', 'UK', 'DE', 'FR', 'JP', 'CA', 'AU', 'IN', 'ES', 'IT'];

function ProductImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-zinc-50">
        <Image size={32} className="text-zinc-300" />
        <span className="text-[10px] text-zinc-400">Click to add image</span>
      </div>
    );
  }
  return <img src={src} alt={alt || ''} className="w-full h-full object-contain p-3" onError={() => setFailed(true)} />;
}

export default function AmazonAssociates() {
  const [associates, setAssociates] = useState([]);
  const [links, setLinks] = useState([]);
  const [reporting, setReporting] = useState(null);
  const [disclosures, setDisclosures] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddTag, setShowAddTag] = useState(false);
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [tagForm, setTagForm] = useState({ region: 'US', tag: '' });

  // Link builder state
  const [urlInput, setUrlInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [linkForm, setLinkForm] = useState({
    asin: '', region: 'US', product_title: '', product_price: '',
    product_image: '', rating: '', original_url: '', disclosure_type: 'standard',
  });
  const [creating, setCreating] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ product_title: '', product_price: '', product_image: '', product_rating: '', disclosure_type: 'standard' });
  const imgInputRef = useRef(null);
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const uploadImage = async (linkId, file) => {
    if (!file) return;
    setUploadingImageFor(linkId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/amazon/links/${linkId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image uploaded');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingImageFor(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assocRes, linksRes, reportRes, discRes] = await Promise.all([
        api.get('/amazon/associates'),
        api.get('/amazon/links'),
        api.get('/amazon/reporting'),
        api.get('/amazon/disclosures'),
      ]);
      setAssociates(assocRes.data || []);
      setLinks(linksRes.data || []);
      setReporting(reportRes.data);
      setDisclosures(discRes.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addTag = async () => {
    if (!tagForm.tag) return;
    try {
      await api.post('/amazon/associates', tagForm);
      toast.success(`${tagForm.region} associate tag added`);
      setShowAddTag(false);
      setTagForm({ region: 'US', tag: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const removeTag = async (region) => {
    try {
      await api.delete(`/amazon/associates/${region}`);
      toast.success(`${region} tag removed`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  // ===== URL PARSE =====
  const parseUrl = async () => {
    if (!urlInput.trim()) { toast.error('Paste an Amazon product URL'); return; }
    setParsing(true);
    setParsed(null);
    try {
      const res = await api.post('/amazon/parse-url', { url: urlInput });
      const d = res.data;
      setParsed(d);
      setLinkForm({
        asin: d.asin || '',
        region: d.region || 'US',
        product_title: d.title || '',
        product_price: d.price || '',
        product_image: d.image || '',
        rating: d.rating || '',
        original_url: d.original_url || urlInput,
        disclosure_type: 'standard',
      });
      toast.success(`Product found: ${d.asin} (${d.region})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse URL');
    } finally {
      setParsing(false);
    }
  };

  // ===== CREATE LINK =====
  const createLink = async () => {
    if (!linkForm.asin || linkForm.asin.length !== 10) {
      toast.error('Valid 10-character ASIN required');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/amazon/links', linkForm);
      if (res.data.policy_violations?.length > 0) {
        toast.warning(`Link created with ${res.data.policy_violations.length} policy flag(s)`);
      } else {
        toast.success('Affiliate link generated!');
      }
      setShowLinkBuilder(false);
      resetBuilder();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const resetBuilder = () => {
    setUrlInput('');
    setParsed(null);
    setLinkForm({ asin: '', region: 'US', product_title: '', product_price: '', product_image: '', rating: '', original_url: '', disclosure_type: 'standard' });
  };

  const deleteLink = async (linkId) => {
    try {
      await api.delete(`/amazon/links/${linkId}`);
      toast.success('Link deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const startEditLink = (link) => {
    setEditingLink(link.link_id);
    setEditForm({ product_title: link.product_title || '', product_price: link.product_price || '', product_image: link.product_image || '', product_rating: link.product_rating || '', disclosure_type: link.disclosure_type || 'standard' });
  };

  const saveEditLink = async () => {
    if (!editingLink) return;
    try {
      await api.put(`/amazon/links/${editingLink}`, editForm);
      toast.success('Link updated');
      setEditingLink(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Affiliate URL copied');
  };

  const refreshAllPrices = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/amazon/links/refresh-all');
      toast.success(res.data.message || 'Prices refreshed');
      fetchData();
    } catch (err) {
      toast.error('Failed to refresh prices');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading Amazon Associates...</div>;

  return (
    <div className="space-y-6" data-testid="amazon-associates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Amazon Associates</h1>
          <p className="text-sm text-zinc-500 mt-1">Paste any Amazon URL to instantly generate affiliate links</p>
        </div>
        <Button onClick={() => { setShowLinkBuilder(true); resetBuilder(); }} data-testid="build-link-btn" className="bg-amber-600 hover:bg-amber-700">
          <Plus size={16} className="mr-2" /> Generate Link
        </Button>
      </div>

      {/* Stats */}
      {reporting && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Regions', value: reporting.total_associates },
            { label: 'Total Links', value: reporting.total_links },
            { label: 'Flagged', value: reporting.flagged_count, color: 'text-amber-400' },
            { label: 'Active Regions', value: Object.keys(reporting.by_region || {}).length },
          ].map((s, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                <p className={`text-lg font-bold mt-0.5 ${s.color || ''}`}>{s.value || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== LINK BUILDER (URL-FIRST) ===== */}
      {showLinkBuilder && (
        <Card className="bg-zinc-900 border-amber-800/30 shadow-lg shadow-amber-900/10" data-testid="link-builder">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-amber-400" /> Generate Affiliate Link
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Step 1: URL Input */}
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider mb-2 block font-medium">Step 1 — Paste Amazon Product URL</label>
              <div className="flex gap-2">
                <Input
                  data-testid="amazon-url-input"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://www.amazon.co.uk/dp/B0XXXXXXX or full product URL..."
                  className="flex-1 bg-zinc-800 border-zinc-700 h-11 font-mono text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && parseUrl()}
                />
                <Button onClick={parseUrl} disabled={parsing} data-testid="parse-url-btn" className="bg-blue-600 hover:bg-blue-700 h-11 px-5 shrink-0">
                  {parsing ? <Spinner size={16} className="animate-spin" /> : <><MagnifyingGlass size={16} className="mr-1.5" /> Parse</>}
                </Button>
              </div>
              <p className="text-[10px] text-zinc-600 mt-1.5">Supports: amazon.com, amazon.co.uk, amazon.de, amzn.to short links, and all Amazon regional stores</p>
            </div>

            {/* Product Preview */}
            {parsed && (
              <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700" data-testid="product-preview">
                <div className="flex gap-4">
                  {linkForm.product_image ? (
                    <img src={linkForm.product_image} alt="" className="w-24 h-24 rounded-lg object-contain bg-white shrink-0" />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-zinc-700 flex items-center justify-center shrink-0"><Image size={28} className="text-zinc-500" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">{linkForm.region}</Badge>
                      <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-600 font-mono">{linkForm.asin}</Badge>
                      <Check size={14} className="text-emerald-400" />
                    </div>
                    {linkForm.product_title ? (
                      <h3 className="text-sm font-medium text-zinc-200 line-clamp-2">{linkForm.product_title}</h3>
                    ) : (
                      <Input data-testid="manual-title" value={linkForm.product_title} onChange={(e) => setLinkForm(p => ({...p, product_title: e.target.value}))} placeholder="Product title (enter manually)" className="bg-zinc-800 border-zinc-700 text-sm h-8 mt-1" />
                    )}
                    <div className="flex items-center gap-3 mt-1.5">
                      {linkForm.product_price && <span className="text-lg font-bold text-emerald-400">{linkForm.product_price}</span>}
                      {linkForm.rating && <span className="text-xs text-amber-400 flex items-center gap-0.5"><Star size={12} weight="fill" /> {linkForm.rating}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Review & Generate */}
            {(parsed || linkForm.asin) && (
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider mb-2 block font-medium">Step 2 — Review & Generate</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">ASIN</label>
                    <Input data-testid="link-asin" value={linkForm.asin} onChange={(e) => setLinkForm(p => ({...p, asin: e.target.value.toUpperCase()}))} className="bg-zinc-800 border-zinc-700 font-mono text-sm h-9" maxLength={10} />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">Region</label>
                    <div className="relative">
                      <select data-testid="link-region" value={linkForm.region} onChange={(e) => setLinkForm(p => ({...p, region: e.target.value}))}
                        className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                        {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">Price</label>
                    <Input value={linkForm.product_price} onChange={(e) => setLinkForm(p => ({...p, product_price: e.target.value}))} placeholder="$29.99" className="bg-zinc-800 border-zinc-700 text-sm h-9" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1 block">Disclosure</label>
                    <div className="relative">
                      <select value={linkForm.disclosure_type} onChange={(e) => setLinkForm(p => ({...p, disclosure_type: e.target.value}))}
                        className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                        {Object.keys(disclosures).map(k => <option key={k} value={k}>{k}</option>)}
                      </select>
                      <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                    </div>
                  </div>
                </div>
                {!linkForm.product_title && !parsed?.title && (
                  <Input value={linkForm.product_title} onChange={(e) => setLinkForm(p => ({...p, product_title: e.target.value}))} placeholder="Product title" className="bg-zinc-800 border-zinc-700 mt-3" />
                )}
                <div className="flex items-center gap-3 mt-4">
                  <Button onClick={createLink} disabled={creating} data-testid="generate-link-btn" className="bg-amber-600 hover:bg-amber-700 h-10 px-6 font-semibold">
                    {creating ? <Spinner size={16} className="animate-spin mr-2" /> : <Lightning size={16} weight="fill" className="mr-2" />}
                    Generate Affiliate Link
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowLinkBuilder(false); resetBuilder(); }} className="text-zinc-400">Cancel</Button>
                </div>
                <p className="text-[10px] text-zinc-600 mt-2 italic">{disclosures[linkForm.disclosure_type] || disclosures.standard}</p>
              </div>
            )}

            {/* Manual ASIN fallback */}
            {!parsed && !linkForm.asin && (
              <div className="pt-3 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-2">Or enter ASIN manually:</p>
                <div className="flex gap-2">
                  <Input value={linkForm.asin} onChange={(e) => setLinkForm(p => ({...p, asin: e.target.value.toUpperCase()}))} placeholder="B0XXXXXXXX" className="bg-zinc-800 border-zinc-700 font-mono w-40" maxLength={10} />
                  <div className="relative w-24">
                    <select value={linkForm.region} onChange={(e) => setLinkForm(p => ({...p, region: e.target.value}))}
                      className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-2 text-sm text-zinc-300 appearance-none">
                      {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <Button size="sm" onClick={createLink} disabled={creating} className="bg-amber-600 hover:bg-amber-700">Generate</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Associate Tags */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe size={18} className="text-blue-400" /> Regional Associate Tags
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={() => setShowAddTag(true)} data-testid="add-tag-btn" className="text-blue-400 hover:text-blue-300">
            <Plus size={16} className="mr-1" /> Add Region
          </Button>
        </CardHeader>
        <CardContent>
          {showAddTag && (
            <div className="mb-4 p-3 border border-zinc-700 rounded-lg bg-zinc-800/50 space-y-3">
              <div className="flex gap-3">
                <div className="relative w-28">
                  <select data-testid="tag-region" value={tagForm.region} onChange={(e) => setTagForm(p => ({...p, region: e.target.value}))}
                    className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                </div>
                <Input data-testid="tag-value" value={tagForm.tag} onChange={(e) => setTagForm(p => ({...p, tag: e.target.value}))} placeholder="your-tag-20" className="flex-1 bg-zinc-800 border-zinc-700" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addTag} data-testid="save-tag-btn" className="bg-blue-600 hover:bg-blue-700">Save Tag</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddTag(false)} className="text-zinc-400">Cancel</Button>
              </div>
            </div>
          )}
          {associates.length === 0 ? (
            <p className="text-sm text-zinc-500">No associate tags yet. Add your Amazon Associate IDs by region.</p>
          ) : (
            <div className="space-y-2">
              {associates.map(a => (
                <div key={a.region} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs text-zinc-300 border-zinc-600 font-mono">{a.region}</Badge>
                    <span className="text-sm text-zinc-300 font-mono">{a.tag}</span>
                    <span className="text-[10px] text-zinc-600">{a.links_created || 0} links</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeTag(a.region)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20">
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Grid — Amazon Best Sellers Style */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <LinkSimple size={20} className="text-amber-400" /> My Products ({links.length})
          </h2>
          {links.length > 0 && (
            <Button onClick={refreshAllPrices} disabled={refreshing} variant="outline" size="sm" data-testid="refresh-prices-btn"
              className="border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/30">
              <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Prices'}
            </Button>
          )}
        </div>

        {links.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
            <CardContent className="p-10 text-center">
              <ShoppingCart size={40} className="mx-auto text-zinc-700 mb-3" />
              <p className="text-zinc-400 font-medium">No products yet</p>
              <p className="text-xs text-zinc-600 mt-1">Paste an Amazon URL to add your first product</p>
              <Button size="sm" onClick={() => setShowLinkBuilder(true)} className="mt-4 bg-amber-600 hover:bg-amber-700">
                <Plus size={14} className="mr-1" /> Add First Product
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(
              links.reduce((groups, link) => {
                const cat = link.category || 'Other';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(link);
                return groups;
              }, {})
            ).sort(([a], [b]) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b))
            .map(([category, catLinks]) => (
              <div key={category} data-testid={`category-${category}`}>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-800">
                  <h3 className="text-base font-bold text-zinc-200">{category}</h3>
                  <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-700">{catLinks.length}</Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {catLinks.map((link, idx) => (
              <div key={link.link_id} data-testid={`link-${link.link_id}`} className="group relative">
                {editingLink === link.link_id ? (
                  <Card className="bg-zinc-900 border-zinc-700">
                    <CardContent className="p-3 space-y-2">
                      <Input value={editForm.product_title} onChange={(e) => setEditForm(p => ({...p, product_title: e.target.value}))} placeholder="Product title" className="bg-zinc-800 border-zinc-700 text-xs" data-testid={`edit-title-${link.link_id}`} />
                      <Input value={editForm.product_price} onChange={(e) => setEditForm(p => ({...p, product_price: e.target.value}))} placeholder="Price" className="bg-zinc-800 border-zinc-700 text-xs" />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={saveEditLink} data-testid={`save-edit-${link.link_id}`} className="bg-blue-600 hover:bg-blue-700 text-[10px] h-7 flex-1">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingLink(null)} className="text-zinc-400 text-[10px] h-7">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-amber-500/30 hover:shadow-lg hover:shadow-amber-900/5 transition-all duration-300">
                    {/* Rank Badge */}
                    <div className="absolute top-0 left-3 z-10">
                      <div className="bg-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded-b-md shadow-md">
                        #{idx + 1}
                      </div>
                    </div>

                    {/* Product Image — clickable to upload */}
                    <div className="relative aspect-square bg-white cursor-pointer overflow-hidden"
                      onClick={() => { imgInputRef.current?.setAttribute('data-link-id', link.link_id); imgInputRef.current?.click(); }}
                      title="Click to change image"
                    >
                      {link.product_image ? (
                        <ProductImage src={link.product_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${link.product_image}` : link.product_image} alt={link.product_title} />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300 gap-2">
                          <Image size={32} className="text-zinc-400" />
                          <span className="text-[10px] text-zinc-400">Click to add image</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/90 rounded-full p-2">
                          <Image size={18} className="text-zinc-800" />
                        </div>
                      </div>
                      {uploadingImageFor === link.link_id && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                          <Spinner size={24} className="text-amber-600 animate-spin" />
                        </div>
                      )}
                      {link.moderation_status === 'flagged' && (
                        <Badge className="absolute top-1 right-1 bg-red-600 text-white text-[8px] px-1 py-0">Flagged</Badge>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-3 space-y-1.5">
                      <a href={link.affiliate_url} target="_blank" rel="noreferrer"
                        className="text-sm text-blue-400 hover:text-amber-400 hover:underline leading-snug line-clamp-3 transition-colors cursor-pointer font-medium"
                        data-testid={`affiliate-url-${link.link_id}`}
                      >
                        {link.product_title || link.asin}
                      </a>

                      {/* Rating Stars */}
                      {link.product_rating && (
                        <div className="flex items-center gap-1">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} size={12} weight="fill" className={n <= Math.round(parseFloat(link.product_rating)) ? 'text-amber-400' : 'text-zinc-600'} />
                            ))}
                          </div>
                          <span className="text-[10px] text-zinc-500">{link.product_rating}</span>
                        </div>
                      )}

                      {/* Price */}
                      {link.product_price ? (
                        <p className="text-lg font-bold text-red-400">{link.product_price}</p>
                      ) : (
                        <button onClick={async (e) => { e.preventDefault(); try { const r = await api.post(`/amazon/links/${link.link_id}/refresh`); if (r.data.product_price) toast.success(`Price: ${r.data.product_price}`); else toast.info('Price not available'); fetchData(); } catch(err) { toast.error('Could not fetch price'); } }}
                          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 py-1">
                          <ArrowsClockwise size={12} /> Fetch Price
                        </button>
                      )}

                      {/* Region + Tag */}
                      <div className="flex items-center gap-1.5 pt-1">
                        <Badge variant="outline" className="text-[9px] text-zinc-400 border-zinc-700 font-mono px-1.5 py-0">{link.region}</Badge>
                        <span className="text-[9px] text-zinc-600 font-mono">{link.tag}</span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 pt-1.5 border-t border-zinc-800 mt-1.5">
                        <Button size="sm" variant="ghost" onClick={() => copyUrl(link.affiliate_url)} className="h-6 px-2 text-[10px] text-blue-400 hover:text-blue-300 flex-1 justify-start">
                          <Copy size={11} className="mr-1" /> Copy Link
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => startEditLink(link)} data-testid={`edit-link-${link.link_id}`} className="h-6 px-1.5 text-zinc-400 hover:text-blue-400">
                          <PencilSimple size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteLink(link.link_id)} className="h-6 px-1.5 text-zinc-400 hover:text-red-400">
                          <Trash size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compliance Notice */}
      <div className="p-3 rounded-lg bg-zinc-900/30 border border-zinc-800/50 text-center">
        <p className="text-[10px] text-zinc-500 italic">As an Amazon Associate, I earn from qualifying purchases. Prices shown are approximate and subject to change.</p>
      </div>

      {/* Hidden file input for image upload */}
      <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
        const file = e.target.files?.[0];
        const linkId = imgInputRef.current?.getAttribute('data-link-id');
        if (file && linkId) uploadImage(linkId, file);
        e.target.value = '';
      }} />
    </div>
  );
}
